import React, { useEffect, useRef, useState } from 'react'

interface Props {
  onChange: (code: string) => void
  maxLength: number
}

export const InputOtp = ({ onChange, maxLength }: Props): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [code, setCode] = useState('')
  const [activeIndex, setActiveIndex] = useState<number | null>(0)

  const isNumber = (value?: unknown): value is number => {
    return value !== null && value !== undefined && typeof value === 'number' && !Number.isNaN(value)
  }

  const isElementInDocumentFocused = (id?: string): boolean => {
    const activeEl = document.activeElement as HTMLInputElement;
  if (
    activeEl &&
    activeEl.tagName === 'INPUT' &&
    id &&
    activeEl.id !== id &&
    activeEl['type'] === 'text'
  )
    return true
  return false
}

  useEffect(() => {
    if (code.length === maxLength) onChange(code)
  }, [code, maxLength])

  useEffect(() => {
    const paste = async (): Promise<void> => {
      if (isElementInDocumentFocused('input-otp')) return
      const text = await navigator.clipboard.readText()
      const code = text.trim().slice(0, maxLength)
      if (code.length === maxLength && code.match(/^\d+$/)) {
        setCode(code)
        setActiveIndex(Math.max(maxLength - 1, code.length))
        inputRef.current?.focus()
      }
    }
    document.addEventListener('paste', paste)
    return (): void => document.removeEventListener('paste', paste)
  }, [])

  return (
    <div className="unbridaled-relative">
      <input
        id="input-otp"
        name="input-otp"
        ref={inputRef}
        value={code}
        autoFocus
        maxLength={6}
        inputMode="numeric"
        autoComplete="one-time-code"
        className="unbridaled-input-opt"
        style={{
          // Fixed px (not rem) so the invisible input's digit spacing stays aligned
          // with the boxed digit UI (App.css's px-based .unbridaled-input-opt-wrapper-item)
          // regardless of the host theme's root font-size — see SHO-298.
          letterSpacing: '44.4px'
        }}
        onChange={(evt: React.ChangeEvent<HTMLInputElement>): void => {
          const changedIndex = evt.target.selectionStart
          if (isNumber(changedIndex)) {
            const value = evt.target.value.slice(0, changedIndex)
            if (isNumber(+value)) setCode(value)
          }
        }}
        onBlur={(): void => setActiveIndex(null)}
        onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>): void => {
          if (isNumber(activeIndex) && evt.key === 'Backspace' && code[activeIndex + 1]) {
            evt.preventDefault()
            setCode(code.slice(0, activeIndex))
          }
        }}
        onSelect={(evt): void => {
          const selectionStart = 'selectionStart' in evt.target ? evt.target.selectionStart : null
          if (isNumber(selectionStart)) {
            setActiveIndex(selectionStart >= maxLength ? maxLength - 1 : selectionStart)
          }
        }}
      />
      <div className="unbridaled-input-opt-wrapper">
        {Array(maxLength)
          .fill(null)
          .map((_, index) => {
            const isActive = activeIndex === index
            const classes = `unbridaled-input-opt-wrapper-item ${
              isActive ? 'border-2 border-ublue' : 'border border-secondary'
            }`
            return (
              <div
                key={index}
                onClick={(): void => {
                  setActiveIndex(index)
                  inputRef.current?.focus()
                  inputRef.current?.setSelectionRange(index, index)
                }}
                inputMode="numeric"
                className={classes}
              >
                {code[index]}
                {activeIndex === index ? (
                  <div
                    className="pointer-events-none absolute inset-0 flex items-center justify-center"
                    style={{
                      transform: code[index] ? 'translateX(8px)' : undefined
                    }}
                  >
                    <div className="unbridaled-caret unbridaled-animate-caret-blink" />
                  </div>
                ) : null}
              </div>
            )
          })}
      </div>
    </div>
  )
}
